import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, PhoneNumberInput } from '@modernloop/shared/components';
import { Alert, Stack, TextField, Typography } from '@mui/material';

import {
  DynamicInterviewMeetingHost,
  InterviewMeetingLocationHost_InterviewMeetingLocationSettingPrefFragment,
  InterviewMeetingLocationSettingPref,
  InterviewMeetingLocationType,
} from 'src/generated/mloop-graphql';

import ZoomUserSelectWithSmartOptions, { SmartOptions } from 'src/entities/Select/ZoomUserSelectWithSmartOptions';

import { InterviewMeetingLocationVariant } from '../types';
import { getInterviewMeetingLocationLabelKey } from '../utils';

import VideoMeetingHost from './VideoMeetingHost';

type Fragments = {
  interviewMeetingLocationSettingPref: InterviewMeetingLocationHost_InterviewMeetingLocationSettingPrefFragment;
};

type Props = {
  disabled: boolean;
  variant?: InterviewMeetingLocationVariant;
  onChange: (pref: InterviewMeetingLocationSettingPref) => void;
};

const InterviewMeetingLocationHost: FCWithFragments<Fragments, Props> = ({
  disabled,
  variant = InterviewMeetingLocationVariant.INTERVIEW,
  interviewMeetingLocationSettingPref,
  onChange,
}) => {
  const { t } = useTranslation();
  const isDebrief = variant === InterviewMeetingLocationVariant.DEBRIEF;
  let alert = <></>;
  if (interviewMeetingLocationSettingPref.dynamicHost === DynamicInterviewMeetingHost.Interviewers) {
    alert = <Alert severity="info">{t('hiring-dashboard-common:common.location.hostInterviewersAlert')}</Alert>;
  } else if (interviewMeetingLocationSettingPref.dynamicHost === DynamicInterviewMeetingHost.Scheduler) {
    alert = <Alert severity="info">{t('hiring-dashboard-common:common.location.hostSchedulerAlert')}</Alert>;
  }

  let label = <Typography variant="subtitle2">{t('hiring-dashboard-common:common.location.hostLabel')}</Typography>;
  let jsx = <></>;

  if (interviewMeetingLocationSettingPref.interviewMeetingLocationType === InterviewMeetingLocationType.Zoom) {
    // Zoom meeting settings currently store the Zoom account ID in hostEmployeeId.
    let zoomUserSelectWithSmartOptionsValue = interviewMeetingLocationSettingPref.remoteVideoMeetingHostUserId;
    if (interviewMeetingLocationSettingPref.dynamicHost === DynamicInterviewMeetingHost.Interviewers) {
      zoomUserSelectWithSmartOptionsValue = SmartOptions.Interviewer;
    } else if (interviewMeetingLocationSettingPref.dynamicHost === DynamicInterviewMeetingHost.Scheduler) {
      zoomUserSelectWithSmartOptionsValue = SmartOptions.Scheduler;
    }

    jsx = (
      <ZoomUserSelectWithSmartOptions
        key={interviewMeetingLocationSettingPref.dynamicHost}
        value={zoomUserSelectWithSmartOptionsValue || undefined}
        panelInterviewerIds={[]}
        disabled={disabled}
        // A debrief is one meeting with one host, so "Interviewers" is not offered (the backend
        // validator rejects the INTERVIEWERS dynamic host for debrief prefs).
        hideInterviewerSmartOption={isDebrief}
        onChange={(option) => {
          const value: string | null | SmartOptions = option.zoomUserId;

          if (value === SmartOptions.Interviewer || value === SmartOptions.Scheduler) {
            onChange({
              interviewMeetingLocationType: interviewMeetingLocationSettingPref.interviewMeetingLocationType,
              dynamicHost:
                value === SmartOptions.Interviewer
                  ? DynamicInterviewMeetingHost.Interviewers
                  : DynamicInterviewMeetingHost.Scheduler,
              videoMeetingLinkScope: interviewMeetingLocationSettingPref.videoMeetingLinkScope,
            });
          } else {
            onChange({
              interviewMeetingLocationType: interviewMeetingLocationSettingPref.interviewMeetingLocationType,
              dynamicHost: undefined,
              remoteVideoMeetingHostUserId: option.zoomUserId,
              videoMeetingLinkScope: interviewMeetingLocationSettingPref.videoMeetingLinkScope,
            });
          }
        }}
      />
    );
  } else if (
    interviewMeetingLocationSettingPref.interviewMeetingLocationType === InterviewMeetingLocationType.GoogleMeet ||
    interviewMeetingLocationSettingPref.interviewMeetingLocationType === InterviewMeetingLocationType.MicrosoftTeams
  ) {
    jsx = (
      <VideoMeetingHost
        key={interviewMeetingLocationSettingPref.interviewMeetingLocationType}
        disabled={disabled}
        variant={variant}
        interviewMeetingLocationSettingPref={interviewMeetingLocationSettingPref}
        onChange={onChange}
      />
    );
  } else if (
    interviewMeetingLocationSettingPref.interviewMeetingLocationType === InterviewMeetingLocationType.CustomLink ||
    interviewMeetingLocationSettingPref.interviewMeetingLocationType === InterviewMeetingLocationType.CustomText
  ) {
    label = (
      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
        {interviewMeetingLocationSettingPref.interviewMeetingLocationType === InterviewMeetingLocationType.CustomLink
          ? t('hiring-dashboard-common:common.location.meetingLinkRequired')
          : undefined}
        {interviewMeetingLocationSettingPref.interviewMeetingLocationType === InterviewMeetingLocationType.CustomText
          ? t('hiring-dashboard-common:common.location.textRequired')
          : undefined}
      </Typography>
    );
    jsx = (
      <TextField
        key={interviewMeetingLocationSettingPref.interviewMeetingLocationType}
        disabled={disabled}
        placeholder={t(
          getInterviewMeetingLocationLabelKey(interviewMeetingLocationSettingPref.interviewMeetingLocationType)
        )}
        inputProps={{ startAdornment: <></> }}
        defaultValue={interviewMeetingLocationSettingPref.customLocation}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onChange({
            interviewMeetingLocationType: interviewMeetingLocationSettingPref.interviewMeetingLocationType,
            customLocation: e.target.value,
            videoMeetingLinkScope: interviewMeetingLocationSettingPref.videoMeetingLinkScope,
          });
        }}
      />
    );
  } else if (
    interviewMeetingLocationSettingPref.interviewMeetingLocationType === InterviewMeetingLocationType.CustomPhone
  ) {
    label = (
      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
        {t('hiring-dashboard-common:common.location.phoneNumberLabel')}
      </Typography>
    );
    jsx = (
      <PhoneNumberInput
        value={interviewMeetingLocationSettingPref.customLocation || ''}
        setPhoneNumber={(phoneNumber: string) => {
          onChange({
            interviewMeetingLocationType: interviewMeetingLocationSettingPref.interviewMeetingLocationType,
            customLocation: phoneNumber,
            videoMeetingLinkScope: interviewMeetingLocationSettingPref.videoMeetingLinkScope,
          });
        }}
      />
    );
  }

  return (
    <Stack spacing={1}>
      <Stack spacing={0.5}>
        {label}
        {jsx}
      </Stack>
      {alert}
    </Stack>
  );
};

InterviewMeetingLocationHost.fragments = {
  interviewMeetingLocationSettingPref: gql`
    ${VideoMeetingHost.fragments.interviewMeetingLocationSettingPref}
    fragment InterviewMeetingLocationHost_interviewMeetingLocationSettingPref on InterviewMeetingLocationSettingPref {
      interviewMeetingLocationType
      videoMeetingLinkScope
      dynamicHost
      hostEmployeeId
      remoteVideoMeetingHostUserId
      remoteVideoMeetingHostUser {
        ... on ZoomUser {
          zoomUserId
          firstName
          lastName
          email
        }
      }
      customLocation
      ...VideoMeetingHost_interviewMeetingLocationSettingPref
    }
  `,
};

export default InterviewMeetingLocationHost;
